import { memo, useState } from 'react';
import OutlineText from './OutlineText';

// Host TV display of the unified game config. The host can cycle each dimension
// directly from the TV via the three toggle buttons. Each click advances to the
// next value in that dimension's cycle and sends an UpdateGameConfig patch to the
// server. Mirrors get disabled buttons (no drive capability).
//
// Cycle orders are ordered arrays so it's trivial to add a third value later.
const TOGGLE_CYCLES = [
    {
        field: "gameType",
        title: "LENGTH",
        values: [
            { value: "quick", label: "Short" },
            { value: "long", label: "Long" },
        ],
    },
    {
        field: "gameMode",
        title: "PLAYERS",
        values: [
            { value: "individuals", label: "Solo" },
            { value: "teams", label: "Teams" },
        ],
    },
    {
        field: "answerMode",
        title: "ANSWERS",
        values: [
            { value: "fuzzy", label: "Say It" },
            { value: "exact", label: "Type It" },
        ],
    },
];

// Per-section help: short intro + a list of modes with their own description. Mirrors the
// phone editor's SECTION_INFO so both screens explain the same thing the same way.
const SECTION_INFO = {
    gameType: {
        title: "Length",
        intro: "How long the round runs.",
        modes: [
            { name: "Short", desc: "Up to 13 phrases. Roughly 15 minutes." },
            { name: "Long",  desc: "Up to 25 phrases. Roughly 30 minutes." },
        ],
    },
    gameMode: {
        title: "Players",
        intro: "How scoring works.",
        modes: [
            { name: "Solo",  desc: "Every player scores for themselves." },
            { name: "Teams", desc: "Players are grouped and share a team score. Needs at least 2 players." },
        ],
    },
    answerMode: {
        title: "Answers",
        intro: "How players answer.",
        modes: [
            { name: "Say It",  desc: "Buzz in, then say or type your answer. Close matches count." },
            { name: "Type It", desc: "Buzz in, then type each letter into slots on your phone. Spelling must be exact." },
        ],
    },
};

const nextValue = (cycle, current) => {
    const idx = cycle.values.findIndex(v => v.value === current);
    const nextIdx = idx < 0 ? 0 : (idx + 1) % cycle.values.length;
    return cycle.values[nextIdx];
};

const currentEntry = (cycle, current) =>
    cycle.values.find(v => v.value === current) ?? cycle.values[0];

const GameConfigView = memo(function GameConfigView({ styles, show, catchphraseData, players, onConfirm, onBack, onUpdate, isMirror }) {
    const gameType = catchphraseData?.gameType || "quick";
    const gameMode = catchphraseData?.gameMode || "individuals";
    const answerMode = catchphraseData?.answerMode || "fuzzy";

    const currentByField = { gameType, gameMode, answerMode };

    const canConfirm = !isMirror && (gameMode !== "teams" || (players?.length ?? 0) >= 2);

    // Which dimension's info modal is open (field key) or null.
    const [infoFor, setInfoFor] = useState(null);
    const activeInfo = infoFor ? SECTION_INFO[infoFor] : null;

    const handleToggle = (cycle) => {
        if (isMirror) return;
        const current = currentByField[cycle.field];
        const next = nextValue(cycle, current);
        onUpdate?.({ [cycle.field]: next.value });
    };

    return (
        <div className={`${styles.gameSelectionSection} ${show ? styles.show : ''}`}>
            <div className={`${styles.titles}`}>
                <OutlineText styles={styles} text="CHOOSE THE" className={styles.subTitle} />
                <OutlineText styles={styles} text={<>GAME<br />SETTINGS</>} className={styles.title} />
            </div>

            <div className={styles.configToggleRow}>
                {TOGGLE_CYCLES.map(cycle => {
                    const current = currentEntry(cycle, currentByField[cycle.field]);
                    const info = SECTION_INFO[cycle.field];
                    return (
                        <div key={cycle.field} className={styles.configToggleCell}>
                            <div className={styles.configToggleHead}>
                                <OutlineText styles={styles} text={info.title} className={styles.configToggleLabel} />
                                <button
                                    type="button"
                                    className={styles.configInfoButton}
                                    aria-label={`About ${info.title}`}
                                    onClick={() => setInfoFor(cycle.field)}
                                    disabled={isMirror}
                                    tabIndex='-1'>
                                    i
                                </button>
                            </div>
                            <button
                                className={`${styles.mainButton} ${styles.smaller} ${styles.configToggleButton}`}
                                onClick={() => handleToggle(cycle)}
                                disabled={isMirror}
                                tabIndex='-1'>
                                <OutlineText styles={styles} text={current.label} />
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className={`${styles.buttons}`}>
                <div className={styles.buttonGroup}>
                    <button className={`${styles.mainButton}`} onClick={onConfirm} disabled={!canConfirm} tabIndex='-1'>
                        <OutlineText styles={styles} text="Start Game" />
                    </button>
                    {gameMode === "teams" && (players?.length ?? 0) < 2 && (
                        <span className={styles.buttonDesc}>Need at least 2 players for teams</span>
                    )}
                </div>
                <div className={styles.buttonGroup}>
                    <button className={`${styles.mainButton} ${styles.grey}`} onClick={onBack} disabled={isMirror} tabIndex='-1'>
                        <OutlineText styles={styles} text="Back" />
                    </button>
                </div>
            </div>

            {activeInfo && (
                <div className={styles.configInfoOverlay} onClick={() => setInfoFor(null)}>
                    <div className={styles.configInfoPanel} onClick={(e) => e.stopPropagation()}>
                        <OutlineText styles={styles} text={activeInfo.title} className={styles.configInfoTitle} />
                        <div className={styles.configInfoBody}>
                            <div className={styles.configInfoIntro}>{activeInfo.intro}</div>
                            <ul className={styles.configInfoModes}>
                                {activeInfo.modes.map(mode => (
                                    <li key={mode.name}>
                                        <span className={styles.configInfoModeName}>{mode.name}.</span>{' '}
                                        {mode.desc}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button
                            type="button"
                            className={`${styles.mainButton} ${styles.grey}`}
                            onClick={() => setInfoFor(null)}
                            tabIndex='-1'>
                            <OutlineText styles={styles} text="Got it" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default GameConfigView;
